import { styled } from '@mui/material/styles';
import { getIdvThemeTokens } from '../themeTokens';

export const FloaterBackdrop = styled('div')(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: 1300,
  background: getIdvThemeTokens(theme).scrim,
}));

export const Floater = styled('form')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    position: 'fixed',
    top: '50%',
    left: '50%',
    zIndex: 1301,
    width: 'min(340px, calc(100vw - 32px))',
    padding: 16,
    display: 'grid',
    gap: 12,
    transform: 'translate(-50%, -50%)',
    border: `1px solid ${t.border}`,
    borderRadius: 12,
    color: t.text,
    background: t.surface,
    colorScheme: t.colorScheme,
    boxShadow: t.overlayShadow,
  };
});

export const FloaterHead = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
});

export const FloaterTitle = styled('h4')({
  margin: 0,
  fontSize: 15,
});

export const CloseButton = styled('button')(({ theme }) => {
  const t = getIdvThemeTokens(theme);
  return {
    width: 26,
    height: 26,
    display: 'grid',
    placeItems: 'center',
    border: `1px solid ${t.border}`,
    borderRadius: 6,
    color: t.textMuted,
    background: t.surface,
    cursor: 'pointer',
    fontSize: 15,
    lineHeight: 1,
    '&:hover': {
      borderColor: t.chipHoverBorder,
      color: t.text,
    },
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
  };
});

export const FloaterField = styled('label')(({ theme }) => ({
  display: 'grid',
  gap: 5,
  color: getIdvThemeTokens(theme).stepLabel,
  fontSize: 11,
  fontWeight: 700,
}));

export const FieldRow = styled('span')({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8,
});

export const RequiredLabel = styled('em')(({ theme }) => ({
  color: getIdvThemeTokens(theme).textMuted,
  fontStyle: 'normal',
  fontWeight: 500,
}));

const fieldStyles = (theme: Parameters<typeof getIdvThemeTokens>[0]) => {
  const t = getIdvThemeTokens(theme);
  return {
    width: '100%',
    boxSizing: 'border-box' as const,
    padding: '8px 10px',
    border: `1px solid ${t.border}`,
    borderRadius: 7,
    outline: 'none',
    color: t.text,
    background: t.surface,
    colorScheme: t.colorScheme,
    fontSize: 12,
    '&::placeholder': {
      color: t.textMuted,
    },
    '&:focus': {
      borderColor: t.violet,
      boxShadow: `0 0 0 3px ${t.violetSoft}`,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  };
};

export const DispositionSelect = styled('select')(({ theme }) => ({
  ...fieldStyles(theme),
  fontWeight: 500,
}));

export const CommentTextarea = styled('textarea')(({ theme }) => ({
  ...fieldStyles(theme),
  minHeight: 82,
  resize: 'vertical',
  fontFamily: 'inherit',
  fontWeight: 400,
}));

export const FloaterActions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
});

export const ActionButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'primary',
})<{ primary?: boolean }>(({ theme, primary = false }) => {
  const t = getIdvThemeTokens(theme);
  return {
    minHeight: 34,
    padding: '7px 12px',
    border: `1px solid ${primary ? t.violet : t.borderStrong}`,
    borderRadius: 7,
    color: primary ? t.markPrimaryText : t.markSecondaryText,
    background: primary ? t.violet : t.surface,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    '&:not(:disabled):hover': primary ? { background: t.violetDark } : { borderColor: t.chipHoverBorder },
    '&:disabled': {
      opacity: 0.45,
      cursor: 'not-allowed',
    },
  };
});
